import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import useCreateAdminUser from '../../api/useCreateAdminUser';
import { AdminButton } from '../../components/AdminButton';
import AdminBackLink from '../../components/atoms/AdminBackLink';
import AdminErrorMessage from '../../components/atoms/AdminErrorMessage';
import AdminFormInput from '../../components/atoms/AdminFormInput';

const ROLE_OPTIONS = [
  { value: 'admin', label: '管理者ユーザー' },
  { value: 'marker_manager', label: '通常ユーザー' },
] as const;

export default function AdminUserCreatePage() {
  const navigate = useNavigate();
  const { mutateAsync: createUser, isPending } = useCreateAdminUser();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('admin');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('名前を入力してください');
      return;
    }
    if (!email.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }
    if (!password) {
      setError('パスワードを入力してください');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('パスワードが一致しません');
      return;
    }
    try {
      setError(null);
      await createUser({
        name: name.trim(),
        email: email.trim(),
        password,
        passwordConfirmation,
        role,
      });
      navigate({ to: '/admin', search: { tab: 'users' } });
    } catch (err) {
      setError(err instanceof Error ? err.message : '作成に失敗しました');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <AdminBackLink to="/admin" search={{ tab: 'users' }} />
      <div className="rounded-md p-md shadow-[2px_2px_4px_0px_rgba(0,0,0,0.1)]">
        <div className="bg-white">
          <div className="flex flex-col gap-3.5">
            <h2 className="my-0 pl-4 text-base font-bold text-[#4188ff]">ユーザーの追加</h2>
            <div className="h-px w-full bg-[#4188ff]" />
          </div>
          <div className="mt-10 flex flex-col gap-6 px-4">
            <div className="w-[400px]">
              <AdminFormInput
                label="名前"
                required
                placeholder="名前を入力してください"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="w-[400px]">
              <AdminFormInput
                label="メールアドレス"
                required
                type="email"
                placeholder="メールアドレスを入力してください"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-sm font-bold text-[#333]">アクセス権限</span>
              <div className="flex gap-4">
                {ROLE_OPTIONS.map((opt) => (
                  <label key={opt.value} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="role"
                      value={opt.value}
                      checked={role === opt.value}
                      onChange={() => setRole(opt.value)}
                      className="size-4 accent-[#4188ff]"
                    />
                    <span className="text-sm text-main">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="h-px bg-border" />
            <div className="w-[604px]">
              <AdminFormInput
                label="パスワード"
                type="password"
                placeholder="パスワードを設定してください"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="w-[604px]">
              <AdminFormInput
                label="パスワード（確認用）"
                type="password"
                placeholder="パスワードを設定してください"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
              />
            </div>
            <AdminErrorMessage message={error} />
            <div className="flex justify-end">
              <AdminButton
                label={isPending ? '追加中...' : '追加する'}
                disabled={isPending}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
